import { cn } from '@/lib/cn';
import type { SkillTool, SkillToolKey } from '@innermate/shared';
import {
  BadgeCheck,
  BarChart3,
  Cable,
  Compass,
  Database,
  FileSearch,
  FileSpreadsheet,
  FileText,
  Globe,
  IdCard,
  Layers,
  Lightbulb,
  ListChecks,
  Mail,
  ScrollText,
  Search,
  Send,
  Sparkles,
  Split,
} from 'lucide-react';
import type { ComponentType } from 'react';
import { ToolConfigEditor } from './ToolConfigEditor';

interface ToolMeta {
  key: SkillToolKey;
  label: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
}

export const TOOL_META: ToolMeta[] = [
  {
    key: 'db.list_tables',
    label: '등록 DB 테이블 목록',
    description:
      '등록된 데이터소스의 테이블·설명·컬럼 수를 나열. describe_table / run_query 전에 먼저 호출.',
    icon: Database,
  },
  {
    key: 'db.describe_table',
    label: 'DB 테이블 상세',
    description: '컬럼 타입·PK/FK·관리자 설명·카디널리티·샘플값. SELECT 쿼리 작성 전에 호출.',
    icon: Database,
  },
  {
    key: 'db.run_query',
    label: 'DB 읽기 전용 SQL',
    description: '등록 데이터소스에 SELECT/WITH 만 허용. 파괴적 문은 거부, LIMIT 자동 주입.',
    icon: Database,
  },
  {
    key: 'excel.list_my_files',
    label: '내 엑셀 파일 목록',
    description: '호출 사용자가 업로드한 "ready" 상태 엑셀 파일 목록. 모드·행수·시트수 포함.',
    icon: FileSpreadsheet,
  },
  {
    key: 'excel.describe',
    label: '엑셀 파일 상세',
    description: '시트·컬럼·추정 타입·행수·미리보기. excel.query / excel.head 전에 호출.',
    icon: FileSpreadsheet,
  },
  {
    key: 'excel.head',
    label: '엑셀 상단 N행',
    description: 'summary / ingested 양쪽 모두에서 시트 상단 N행 fetch (최대 100).',
    icon: FileSpreadsheet,
  },
  {
    key: 'excel.query',
    label: '엑셀 SQL',
    description: 'ingested 모드 엑셀 파일에 SELECT. excel_uploads 스키마만 접근 가능.',
    icon: FileSpreadsheet,
  },
  {
    key: 'doc.read',
    label: '첨부 문서 읽기',
    description:
      '첨부 문서(pdf/docx/pptx/hwp/hwpx/txt/md) 변환 마크다운을 offset/limit 으로 읽기. 큰 문서는 doc.search 로 위치 파악 후 호출.',
    icon: FileText,
  },
  {
    key: 'doc.search',
    label: '첨부 문서 검색',
    description:
      '첨부 문서 변환 마크다운에서 키워드 검색 — 매칭 스니펫과 문자 위치 반환. 큰 문서 탐색용.',
    icon: FileSearch,
  },
  {
    key: 'web.fetch_url',
    label: '웹 페이지 가져오기',
    description:
      '공개 웹 페이지를 헤드리스 브라우저로 가져와 텍스트/마크다운/HTML 로 반환. 사설 IP 차단 (SSRF).',
    icon: Globe,
  },
  {
    key: 'web.search_grounded',
    label: '웹 검색 (Google AI Grounding)',
    description:
      'Gemini Grounding 으로 외부 웹 검색 + 합성 답변과 출처 반환. 호출당 비용 발생 — provider=gemini endpoint 가 필요.',
    icon: Search,
  },
  {
    key: 'http.list_endpoints',
    label: '사내 API Endpoint 목록',
    description:
      '이 Skill 에 허용된 HTTP endpoint 들의 메타 (name, baseUrl, description, allowedMethods) 반환.',
    icon: Cable,
  },
  {
    key: 'http.request',
    label: '사내 API 호출',
    description:
      '등록된 Endpoint 에 GET/POST/PUT/DELETE/PATCH 호출. 인증·쿠키는 서버에서 자동 주입.',
    icon: Cable,
  },
  {
    key: 'analysis.verify_tag',
    label: 'Insight Agent 질의유형 유효성 검증',
    description:
      'Insight Agent 호출로 사용자 질문을 분석 태그 분류 체계와 대조. config.searchYn 으로 현황분석(N) / 데이터탐색(Y) 분기. 점수 차가 크면 HITL 로 사용자에게 태그 선택을 받음.',
    icon: BadgeCheck,
  },
  {
    key: 'analysis.verify_detail',
    label: 'Insight Agent 상세정보 유효성 검증',
    description:
      'verify_tag 다음 단계 (현황분석/데이터탐색 공통) — 확정된 tag 와 질문을 다시 검증해 답변 생성에 필요한 상세 정보가 모두 있는지 체크. 부족하면 사용자에게 안내 후 중단.',
    icon: ListChecks,
  },
  {
    key: 'analysis.apply_task',
    label: '현황분석 데이터 분석 요청',
    description:
      'verify_detail 통과 후 호출 — Insight Agent 의 streaming endpoint 로 분석을 접수하고 taskId 를 받아 다음 단계에 전달.',
    icon: Send,
  },
  {
    key: 'analysis.get_data',
    label: '현황분석 결과 데이터 수신',
    description:
      'apply_task 가 발급한 taskId 로 결과 데이터를 polling 해서 받음. 서버가 /data 200 까지 최대 5분 대기. result_obj(텍스트+테이블+차트) 와 추천 질문 포함된 응답 반환.',
    icon: BarChart3,
  },
  {
    key: 'datasearch.split',
    label: '데이터탐색 질문 분리',
    description:
      'verify_detail 통과 후 호출 — finalQuestion 을 태그별 sub-question 배열로 분리. LLM 인자 없음 (ctx 자동 주입). 분리 태그는 config.tag 로 박힘.',
    icon: Split,
  },
  {
    key: 'datasearch.analyze',
    label: '데이터탐색 데이터 분석',
    description:
      'split 결과 questions 를 받아 태그별 SQL 병렬 실행 + 1차 인사이트 생성. status=empty/timeout/skipped 분기는 사용자에게 안내 후 turn 종료.',
    icon: Layers,
  },
  {
    key: 'datasearch.expand',
    label: '데이터탐색 확장 분석',
    description:
      'analyze 의 data/insight 를 ctx 로 자동 carry — LLM 인자 없음. 확장 질문 생성·실행 후 각 인사이트 반환.',
    icon: Sparkles,
  },
  {
    key: 'datasearch.total_insight',
    label: '데이터탐색 종합 인사이트',
    description:
      'analyze.insight + expand.results[].insight 를 서버가 합성해 종합 인사이트 생성. LLM 인자 없음.',
    icon: Lightbulb,
  },
  {
    key: 'datasearch.grounding',
    label: '데이터탐색 외부 동향 조사',
    description:
      '데이터탐색의 선택적 단계 — verify_detail 통과 후 split/analyze/expand 와 무관하게 단독 호출 가능한 외부 동향 조사. LLM 인자 없음.',
    icon: Compass,
  },
  {
    key: 'email.send',
    label: '이메일 발송',
    description:
      'SMTP 경유로 메일을 발송합니다. 발송 전 사용자 확인(HITL) 모달이 표시되며, 허용 도메인과 최대 수신자 수를 설정할 수 있습니다. SMTP 설정이 필요합니다.',
    icon: Mail,
  },
  {
    key: 'user.get_attributes',
    label: '본인 사용자 속성 조회',
    description:
      '본인 (호출자) 사용자 속성 (사번/팀/직급 등 admin 정의) 을 read-only 로 조회. 매 turn 자동 주입 없이 on-demand. system prompt 에 "내/나의/우리 팀" 같은 본인 지칭 표현에만 호출하라는 가이드 한 줄을 함께 작성하세요.',
    icon: IdCard,
  },
  {
    key: 'skill.call',
    label: '다른 Skill 에 위임 (라우터)',
    description:
      '이 skill 을 라우터로 동작시켜 사용자 질문을 지정한 다른 skill 에 위임. 아래에서 호출 가능한 sub-skill 을 최소 1개 선택해야 함. system prompt 에 어떤 질문을 어느 skill 로 보낼지 분기 규칙을 함께 작성하세요.',
    icon: Split,
  },
  {
    key: 'prompt.run',
    label: '응답 프롬프트 실행',
    description:
      '/admin/response-controls/prompts 에 등록된 응답 프롬프트 (slug) 를 sub-LLM 으로 실행해 결과 텍스트를 받음. 아래에서 호출 허용 slug 를 최소 1개 선택해야 함. system 메시지에 사용 가능 slug + 변수 + variant 정보가 자동 주입됩니다.',
    icon: ScrollText,
  },
];

interface ToolPickerProps {
  tools: SkillTool[];
  onChange: (tools: SkillTool[]) => void;
}

export function ToolPicker({ tools, onChange }: ToolPickerProps) {
  const selected = new Map(tools.map((t) => [t.toolKey, t.config] as const));

  const toggle = (key: SkillToolKey) => {
    if (selected.has(key)) {
      onChange(tools.filter((t) => t.toolKey !== key));
    } else {
      onChange([...tools, { toolKey: key, config: {} }]);
    }
  };

  const updateConfig = (key: SkillToolKey, config: Record<string, unknown>) => {
    onChange(tools.map((t) => (t.toolKey === key ? { ...t, config } : t)));
  };

  return (
    <div className="space-y-3">
      {TOOL_META.map((meta) => {
        const isSelected = selected.has(meta.key);
        const Icon = meta.icon;
        return (
          <div
            key={meta.key}
            className={cn(
              'rounded-lg border bg-surface transition',
              isSelected ? 'border-accent/50' : 'border-border',
            )}
          >
            <label className="flex items-start gap-3 p-3 cursor-pointer">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => toggle(meta.key)}
                className="mt-1 size-4 accent-accent"
              />
              <Icon className="mt-0.5 size-4 text-foreground-muted shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-medium text-sm">{meta.label}</span>
                  <span className="font-mono text-[11px] text-foreground-muted">{meta.key}</span>
                </div>
                <p className="mt-0.5 text-xs text-foreground-muted">{meta.description}</p>
              </div>
            </label>
            {isSelected ? (
              <div className="border-t border-border px-3 py-3 bg-surface-muted/20">
                <ToolConfigEditor
                  toolKey={meta.key}
                  config={selected.get(meta.key) ?? {}}
                  onChange={(c) => updateConfig(meta.key, c)}
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
